import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { mergeMaterialIds, type IReplaceLayerResult } from '@/framework';
import { createBackgroundLayer } from '@/layers/background-layer/utils';
import { createImageLayer } from '@/layers/image-layer/utils/layer';
import { Layer } from '@/layers/layer';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { getMainLayerProps } from '@/utils/layer-kit';
import {
    LayerType,
    PluginType,
    type IExportPlugin,
    type IImageLayerProps,
    type ILayerProps,
    type IMainLayerProps,
    type IMainLayerScope,
    type ISize,
} from '@/version-types';
import mainLayerBehavior from './behavior';
import { checkNeedMergeCanvas } from './utils';

export { EdgelessMainLayer, type IMainCanvasResultOptions } from './edgeless-layer';
export * from './plugins';

export class MainLayer extends Layer {
    scope?: IMainLayerScope;

    /** 设置主图编辑时，会合成整个画布，需要合并的图层类型*/
    mergeLayerType: LayerType[] = [];

    private originStageLayers: ILayerProps[] = [];
    private mergeReplaceIds: string[] = [];
    private replaceResult: IReplaceLayerResult | null = null;

    async enterMergeMode(targetLayer: ILayerProps): Promise<ILayerProps> {
        if (targetLayer.layerType !== LayerType.MAIN_LAYER) return targetLayer;

        const originIndex = this.framework.service.frame.getActiveFrameId();
        let layers = this.framework.service.frame.getLayers(originIndex);

        if (this.scope === 'canvas' && this.mergeLayerType.length > 0) {
            layers = layers.filter((layer) => this.mergeLayerType.includes(layer.layerType));
        }

        if (!checkNeedMergeCanvas(this.scope, layers)) {
            return targetLayer;
        }

        const backgroundLayer = layers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
        const mainLayer = layers.find((layer) => layer.layerType === LayerType.MAIN_LAYER);

        if (!backgroundLayer || !mainLayer) return targetLayer;

        const newBackgroundLayer = createBackgroundLayer({
            backgroundColor: '#00000000',
            width: backgroundLayer.width,
            height: backgroundLayer.height,
        });

        // drawLayers 会归一化图层位置，必须隔离舞台中的原始图层数据
        const canvasImage = await this.framework.service.result.drawLayers(cloneDeep(layers), 'png');
        const base64 = canvasImage.toDataURL('image/png');
        const url = base64Kit.getCode(base64);

        const newMainLayer = this.createMainLayer(url, canvasImage.width, canvasImage.height);
        const mergeLayers = layers.filter((layer) => !layer.hidden);
        const replaceIds = mergeLayers.map((layer) => layer.id);

        // 合并分离图层 materialIds
        let materialIds: Record<string, string[]> = {};

        layers.forEach((layer) => {
            // 合并所有图层的 materialIds 到主图
            materialIds = mergeMaterialIds(materialIds, layer.materialIds);
        });

        newMainLayer.materialIds = materialIds;

        this.originStageLayers = mergeLayers;
        this.mergeReplaceIds = replaceIds;
        this.replaceResult = this.framework.service.stage.muteReplaceLayer(replaceIds, [
            newBackgroundLayer,
            newMainLayer,
        ]);

        return newMainLayer;
    }

    applyMergeMode(newMainLayer: ILayerProps, func?: string) {
        if (!this.replaceResult) return;
        if (this.originStageLayers.length === 0) return;

        const originIndex = this.framework.service.frame.getActiveFrameId();
        const layers = this.framework.service.frame.getLayers(originIndex);
        const backgroundLayer = layers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);

        if (!backgroundLayer) return;

        this.framework.service.frame.addOrUpdateLayer(newMainLayer);
        this.framework.service.stage.applyReplaceLayer(
            this.mergeReplaceIds,
            this.replaceResult,
            [backgroundLayer, newMainLayer],
            '',
            func,
        );
        this.replaceResult = null;
        this.originStageLayers = [];
        this.mergeReplaceIds = [];
        return true;
    }

    exitMergeMode() {
        if (this.originStageLayers.length === 0) return;

        if (!this.replaceResult) return;
        this.framework.service.stage.muteRestoreReplaceLayer(this.replaceResult);
        this.replaceResult = null;
        this.originStageLayers = [];
        this.mergeReplaceIds = [];
        return true;
    }

    getMainLayer(index?: number) {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        return this.framework.service.frame.getMainLayer(index === undefined ? originIndex : index);
    }

    getResultCanvasAsync(layer: ILayerProps, stageSize: ISize) {
        return this.framework.service.behavior.get(layer)?.getResultCanvasAsync?.(layer, stageSize);
    }

    async updateMainLayerURL(url: string, trackFunc?: string, index?: number) {
        const mainLayer = this.getMainLayer(index);
        if (!mainLayer) return;
        const { width, height } = await getImgInfoAsync(url);
        const newLayer = cloneDeep(mainLayer);
        const data = newLayer.data as IImageLayerProps;
        data.url = url;
        data.naturalHeight = height;
        data.naturalWidth = width;
        this.updateLayer(newLayer, mainLayer, trackFunc);
    }
    updateLayer(newLayer: ILayerProps, oldLayer?: ILayerProps | null, trackFunc?: string) {
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, newLayer, trackFunc);
        }
    }

    createMainLayer = (
        url: string,
        width: number,
        height: number,
        isTransparent = false,
        naturalWidth?: number,
        naturalHeight?: number,
    ) => {
        return getMainLayerProps({
            width,
            height,
            data: {
                url,
                maskURL: '',
                stretchOption: 2,
                naturalWidth: naturalWidth ?? width,
                naturalHeight: naturalHeight ?? height,
                isTransparent,
                imageTransform: {
                    offsetX: 0,
                    offsetY: 0,
                    rotate: 0,
                    scale: 1,
                    flipH: false,
                    flipV: false,
                    widthRatio: 1,
                    heightRatio: 1,
                },
                maskType: 0,
                cutoutMaskURL: '',
            },
        });
    };

    // 创建空主图
    createTransparentMainLayer(width: number, height: number, customImageURL?: string) {
        let url = customImageURL || '';
        if (!url) {
            const { canvas } = createCanvas(width, height);
            const base64 = canvas.toDataURL();
            url = base64Kit.getCode(base64, uuid());
        }

        return this.createMainLayer(url, width, height, true);
    }

    convertToImageLayer(transparentImageURL?: string, index?: number) {
        const mainLayer = this.getMainLayer(index)!;
        const transparentMainLayer = this.createTransparentMainLayer(
            mainLayer.width,
            mainLayer.height,
            transparentImageURL,
        );

        transparentMainLayer.id = mainLayer.id;

        const mainLayerData = mainLayer.data as IMainLayerProps;
        const imageLayer = createImageLayer(mainLayerData.url, mainLayer.width, mainLayer.height, {
            naturalWidth: mainLayerData.naturalWidth,
            naturalHeight: mainLayerData.naturalHeight,
        });

        if (mainLayer.extraInfo?.fromDemo) {
            imageLayer.extraInfo = {
                ...imageLayer.extraInfo,
                fromDemo: mainLayer.extraInfo?.fromDemo,
            };
        }

        // 合并分离图层 materialIds
        imageLayer.materialIds = mainLayer.materialIds;

        return [transparentMainLayer, imageLayer] as const;
    }
}

/**
 * 主图图层插件
 */
export const mainLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.MAIN_LAYER,
    createInstance({ identity }) {
        return [{ key: 'MainLayer', value: new MainLayer(identity) }];
    },
    createBehavior() {
        return mainLayerBehavior;
    },
};
